import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowLeft, Sparkles, CheckCircle2, AlertTriangle, TrendingUp, Clock, CalendarClock } from "lucide-react";
import { useClinic } from "@/store/ClinicContext";
import { INITIAL_DOCTORS } from "@/data/cfmData";
import { formatBRL, WEEK_DAYS, normBlocks, minutesOf, DEFAULT_DURATION } from "@/data/mockData";

const DEMAND = { seg: "alta", ter: "média", qua: "alta", qui: "média", sex: "alta", sab: "baixa" };

export default function Diagnostico() {
  const { appointments, availability, durations } = useClinic();

  const audits = useMemo(() => {
    return INITIAL_DOCTORS.map((d) => {
      const av = availability[d.id] || {};
      const dur = durations?.[d.id] || DEFAULT_DURATION;
      let horas = 0;
      let diasAbertos = 0;
      Object.values(av).forEach((day) => {
        const blks = [...normBlocks(day?.manha, "manha"), ...normBlocks(day?.tarde, "tarde")];
        const h = blks.reduce((s, b) => s + Math.max(0, (minutesOf(b.to) - minutesOf(b.from)) / 60), 0);
        if (h > 0) diasAbertos++;
        horas += h;
      });
      diasAbertos = diasAbertos || 1;
      const horasDia = horas / diasAbertos || 0;
      const mine = appointments.filter((a) => a.doctorId === d.id && a.status !== "cancelado");
      const atendimentos = mine.length;
      const faturamento = mine.reduce((s, a) => s + (a.value || 0), 0);
      const repasse = (faturamento * d.repasse) / 100;
      const custoClinica = faturamento - repasse;
      const ocupacao = horas > 0 ? Math.min(100, Math.round(((atendimentos * (dur / 60)) / horas) * 100)) : 0;
      let verdict;
      if (ocupacao >= 80) verdict = { tone: "green", text: "Altamente Rentável e Produtivo — mantém taxa de ocupação ideal e margem saudável para a clínica." };
      else if (ocupacao >= 40) verdict = { tone: "yellow", text: "Atenção: ocupação moderada. Há janelas ociosas — recomenda-se redistribuir horários para elevar a produtividade." };
      else verdict = { tone: "yellow", text: "Alta ociosidade detectada. Reavalie a disponibilidade aberta versus a demanda real de agendamentos." };
      const isFull = (k) => normBlocks(av[k]?.manha, "manha").length > 0 && normBlocks(av[k]?.tarde, "tarde").length > 0;
      const isOpen = (k) => normBlocks(av[k]?.manha, "manha").length > 0 || normBlocks(av[k]?.tarde, "tarde").length > 0;
      const recOpen = WEEK_DAYS.find((w) => DEMAND[w.key] === "alta" && !isFull(w.key));
      const recIdle = WEEK_DAYS.find((w) => DEMAND[w.key] === "baixa" && isOpen(w.key));
      return { d, horas, horasDia, dur, atendimentos, faturamento, repasse, custoClinica, ocupacao, verdict, av, recOpen, recIdle };
    });
  }, [appointments, availability, durations]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-5 py-3.5 md:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600"><Activity className="h-5 w-5 text-white" strokeWidth={2.5} /></div>
            <div className="flex items-center gap-2">
              <span className="font-heading text-lg font-extrabold tracking-tight text-slate-900">VeloClin</span>
              <span className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">Diagnóstico IA</span>
            </div>
          </div>
          <Link to="/agenda" data-testid="back-to-agenda-diag" className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"><ArrowLeft className="h-4 w-4" /> Agenda</Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-5 py-6 md:px-8 md:py-8">
        <div className="mb-6 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600"><Sparkles className="h-4 w-4 text-white" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Diagnóstico de Rentabilidade por IA</h1>
            <p className="text-sm text-slate-500">Auditoria automática de produtividade e eficiência por médico.</p>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          {audits.map(({ d, horas, horasDia, atendimentos, faturamento, repasse, custoClinica, ocupacao, verdict, av, recOpen, recIdle }) => (
            <div key={d.id} data-testid={`audit-card-${d.id}`} className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <div className="flex items-center gap-3">
                <img src={d.avatar} alt={d.name} className="h-11 w-11 rounded-full object-cover ring-1 ring-slate-200" />
                <div><p className="font-heading text-base font-bold text-slate-900">{d.name}</p><p className="text-xs text-slate-400">{d.specialty} · repasse {d.repasse}%</p></div>
              </div>

              <div className="mt-5">
                <div className="mb-1.5 flex items-center justify-between text-sm">
                  <span className="text-slate-500">Taxa de ocupação ({atendimentos} atend. / ~{Math.round(horasDia)}h/dia · {Math.round(horas)}h/sem)</span>
                  <span data-testid={`audit-ocupacao-${d.id}`} className="font-mono-num font-bold text-slate-900">{ocupacao}%</span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className={`h-full rounded-full ${ocupacao >= 80 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${ocupacao}%` }} />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3"><p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Faturamento gerado</p><p className="font-mono-num mt-1 text-base font-bold text-slate-900">{formatBRL(faturamento)}</p></div>
                <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3"><p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Margem da clínica</p><p className="font-mono-num mt-1 text-base font-bold text-emerald-600">{formatBRL(custoClinica)}</p></div>
              </div>

              <div className={`mt-4 flex items-start gap-2.5 rounded-xl border p-3.5 ${verdict.tone === "green" ? "border-emerald-200/60 bg-emerald-50/60" : "border-amber-200/60 bg-amber-50/60"}`}>
                {verdict.tone === "green" ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
                <div>
                  <p className={`text-xs font-bold ${verdict.tone === "green" ? "text-emerald-700" : "text-amber-700"}`}>Veredito da IA</p>
                  <p className={`text-xs leading-relaxed ${verdict.tone === "green" ? "text-emerald-700" : "text-amber-700"}`}>{verdict.text}</p>
                </div>
              </div>

              {/* IA por Período */}
              <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50/60 p-3.5" data-testid={`ia-periodo-${d.id}`}>
                <p className="mb-2.5 flex items-center gap-1.5 text-xs font-bold text-slate-700">
                  <CalendarClock className="h-3.5 w-3.5 text-blue-600" /> IA por Período — ocupação por dia
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {WEEK_DAYS.map((w) => {
                    const mb = normBlocks(av[w.key]?.manha, "manha");
                    const tb = normBlocks(av[w.key]?.tarde, "tarde");
                    const fmt = (arr) => arr.map((b) => `${b.from}–${b.to}`).join(", ");
                    return (
                      <div key={w.key} className="flex flex-col items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5">
                        <span className="text-[10px] font-semibold text-slate-500">{w.label.slice(0, 3)}</span>
                        <div className="flex gap-1">
                          <span className={`h-2 w-2 rounded-full ${mb.length ? "bg-emerald-500" : "bg-slate-200"}`} title={mb.length ? `Manhã ${fmt(mb)}` : "Manhã fechada"} />
                          <span className={`h-2 w-2 rounded-full ${tb.length ? "bg-emerald-500" : "bg-slate-200"}`} title={tb.length ? `Tarde ${fmt(tb)}` : "Tarde fechada"} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 space-y-1.5">
                  {recOpen && (
                    <p className="text-xs text-blue-700">
                      <TrendingUp className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />
                      Melhor horário para abrir: <b>{recOpen.label}</b> (demanda {DEMAND[recOpen.key]}) — elevaria a ocupação.
                    </p>
                  )}
                  {recIdle && (
                    <p className="text-xs text-amber-700">
                      <Clock className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />
                      Turno possivelmente ocioso: <b>{recIdle.label}</b> (baixa procura) — considere realocar.
                    </p>
                  )}
                  {!recOpen && !recIdle && (
                    <p className="text-xs text-slate-400">Distribuição de turnos equilibrada para a demanda estimada.</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
